import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { listPageHref } from '@/seo/urls';

/**
 * Crawlable pagination for the catalog lists (web plan W0.11).
 *
 * Every control is a real `<a href>` (react-router `<Link>`): Previous, Next
 * and the page numbers, so a crawler that never scrolls reaches every page of
 * the list, and so every item. Page 1 links to the bare path, never
 * `?page=1`. Infinite scroll stays on top for people (usePaginatedList).
 */

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

/** Pages on each side of the first and last page on screen. */
const SIBLINGS = 2;

type Item = number | 'gap';

/**
 * Page 1, the last page, and SIBLINGS pages around the first and the last page
 * on screen, in order. A hole of exactly one page shows that page; a longer
 * one becomes a gap ("…").
 */
function pageItems(page: number, throughPage: number, lastPage: number): Item[] {
  const pages = new Set<number>([1, lastPage]);
  const around = (centre: number) => {
    for (let n = centre - SIBLINGS; n <= centre + SIBLINGS; n += 1) {
      if (n >= 1 && n <= lastPage) pages.add(n);
    }
  };
  around(page);
  around(throughPage);

  const items: Item[] = [];
  let previous = 0;
  for (const n of [...pages].sort((a, b) => a - b)) {
    if (n - previous === 2) items.push(n - 1);
    else if (n - previous > 2) items.push('gap');
    items.push(n);
    previous = n;
  }
  return items;
}

const control =
  'inline-flex h-10 min-w-10 items-center justify-center gap-1 rounded-xl px-3 font-semibold transition-colors';
const idle = 'border border-line text-ink hover:border-primary/40 hover:bg-primary/10 hover:text-primary-strong';

export interface PaginationProps {
  /** Path of page 1, e.g. `/brands`; page N links to `{basePath}?page=N`. */
  basePath: string;
  /** The page the URL names: the first page on screen. */
  page: number;
  /**
   * The last page on screen, once infinite scroll has appended pages after
   * `page`. Next points past it. Defaults to `page`.
   */
  throughPage?: number;
  /** The list's last page. Nothing renders for a one-page list. */
  lastPage: number;
  /** Accessible name of the navigation landmark. */
  label?: string;
  /** The controls' words (a W3 hub passes its catalogue's, web plan W3.0); English by default. */
  labels?: Partial<PaginationLabels>;
  className?: string;
}

export interface PaginationLabels {
  previous: string;
  next: string;
  /** Accessible name of the link to page `page`. */
  page: (page: number) => string;
}

const DEFAULT_LABELS: PaginationLabels = {
  previous: 'Previous',
  next: 'Next',
  page: (page) => `Page ${page}`,
};

/** Previous / numbered / Next links for a paginated list. */
export function Pagination({
  basePath,
  page,
  throughPage = page,
  lastPage,
  label = 'Pagination',
  labels,
  className,
}: PaginationProps) {
  if (lastPage <= 1 || page < 1 || page > lastPage) return null;
  const words = { ...DEFAULT_LABELS, ...labels };
  const through = Math.min(Math.max(throughPage, page), lastPage);
  const href = (n: number) => listPageHref(basePath, n);

  return (
    <nav aria-label={label} className={cn('mt-10 flex justify-center', className)}>
      <ul className="flex flex-wrap items-center justify-center gap-2 text-sm">
        {page > 1 && (
          <li>
            <Link to={href(page - 1)} rel="prev" className={cn(control, idle)}>
              <ChevronLeft size={16} aria-hidden="true" />
              {words.previous}
            </Link>
          </li>
        )}
        {pageItems(page, through, lastPage).map((item, index) =>
          item === 'gap' ? (
            <li key={`gap-${index}`} aria-hidden="true" className="px-1 text-ink-tertiary">
              …
            </li>
          ) : item === page ? (
            <li key={item}>
              <span aria-current="page" className={cn(control, 'bg-primary text-white')}>
                {item}
              </span>
            </li>
          ) : (
            <li key={item}>
              <Link to={href(item)} aria-label={words.page(item)} className={cn(control, idle)}>
                {item}
              </Link>
            </li>
          ),
        )}
        {through < lastPage && (
          <li>
            <Link to={href(through + 1)} rel="next" className={cn(control, idle)}>
              {words.next}
              <ChevronRight size={16} aria-hidden="true" />
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
}

export interface PreviousPageLinkProps {
  basePath: string;
  /** The page the URL names; nothing renders on page 1. */
  page: number;
  className?: string;
}

/**
 * "Previous page" above a list opened on page N ≥ 2. Infinite scroll keeps
 * pushing the bottom Pagination down, so without this the earlier pages could
 * not be reached from the top.
 */
export function PreviousPageLink({ basePath, page, className }: PreviousPageLinkProps) {
  if (page <= 1) return null;
  return (
    <div className={cn('mb-4', className)}>
      <Link
        to={listPageHref(basePath, page - 1)}
        rel="prev"
        className="inline-flex items-center gap-1 text-sm font-semibold text-primary-strong hover:underline"
      >
        <ChevronLeft size={16} aria-hidden="true" />
        Previous page
      </Link>
    </div>
  );
}

export interface NoPageNoticeProps {
  basePath: string;
  /** The page as the URL wrote it (`999`, `abc`). */
  requested: string;
  /** The notice's words (a W3 hub passes its catalogue's); English by default. */
  labels?: { noPage: (page: string) => string; firstPage: string };
  className?: string;
}

/** Longest `?page=` value echoed back; the rest is cut. */
const REQUESTED_MAX = 24;

/**
 * "No page N — go to page 1": what a list shows for a page that does not
 * exist (not a whole number from 1, or past the last page). The page is
 * `noindex`; the W1 server answers these with a 404.
 */
export function NoPageNotice({ basePath, requested, labels, className }: NoPageNoticeProps) {
  const shown =
    requested.length > REQUESTED_MAX ? `${requested.slice(0, REQUESTED_MAX)}…` : requested;
  return (
    <div className={cn('flex flex-col items-center px-6 py-16 text-center', className)}>
      <h2 className="text-lg font-bold text-ink">
        {labels ? labels.noPage(shown) : <>No page {shown} —</>}{' '}
        <Link to={basePath} className="text-primary-strong underline hover:no-underline">
          {labels ? labels.firstPage : 'go to page 1'}
        </Link>
      </h2>
    </div>
  );
}

export default Pagination;
